import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Spinner } from '@elka/ui';
import { Layout } from './components/Layout';
import { LoginPage } from './features/auth/components/LoginPage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { DealsPage } from './pages/DealsPage';
import { useGmailEnabled } from './hooks/useGmailEnabled';

// Eager: shell, auth, and the landing page (DealsPage). Everything else is code-split so the
// initial bundle stays lean — the deal workspace, pricing stages, bids, bills, pipeline (dnd-kit)
// and smart-inbox each load on demand. Lazy children render inside Layout, whose <Outlet> is
// wrapped in <Suspense>; the public supplier route gets its own boundary below.
const QuotePricingPage = lazy(() =>
  import('./pages/QuotePricingPage').then((m) => ({ default: m.QuotePricingPage }))
);
const SupplierPricingPage = lazy(() =>
  import('./pages/SupplierPricingPage').then((m) => ({ default: m.SupplierPricingPage }))
);
const QuotesPage = lazy(() =>
  import('./pages/QuotesPage').then((m) => ({ default: m.QuotesPage }))
);
const QuickQuotePage = lazy(() =>
  import('./pages/QuickQuotePage').then((m) => ({ default: m.QuickQuotePage }))
);
const BillsPage = lazy(() => import('./pages/BillsPage').then((m) => ({ default: m.BillsPage })));
const BillPage = lazy(() => import('./pages/BillPage').then((m) => ({ default: m.BillPage })));
const QuickBillPage = lazy(() =>
  import('./pages/QuickBillPage').then((m) => ({ default: m.QuickBillPage }))
);
const SettingsPage = lazy(() =>
  import('./pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);
const InboundEmailsPage = lazy(() =>
  import('./pages/InboundEmailsPage').then((m) => ({ default: m.InboundEmailsPage }))
);
const BoqIntakePage = lazy(() =>
  import('./pages/BoqIntakePage').then((m) => ({ default: m.BoqIntakePage }))
);
const LaborCalculationPage = lazy(() =>
  import('./pages/LaborCalculationPage').then((m) => ({ default: m.LaborCalculationPage }))
);
const BidRequestDetailPage = lazy(() =>
  import('./pages/BidRequestDetailPage').then((m) => ({ default: m.BidRequestDetailPage }))
);
const BidRequestEntryPage = lazy(() =>
  import('./pages/BidRequestEntryPage').then((m) => ({ default: m.BidRequestEntryPage }))
);
const BidRequestsIndexPage = lazy(() =>
  import('./pages/BidRequestsIndexPage').then((m) => ({ default: m.BidRequestsIndexPage }))
);
const SupplierQuotePage = lazy(() =>
  import('./pages/SupplierQuotePage').then((m) => ({ default: m.SupplierQuotePage }))
);
const PricingMatrixPage = lazy(() =>
  import('./features/pricing-workspace/components/PricingMatrixPage').then((m) => ({
    default: m.PricingMatrixPage,
  }))
);
const NewDealOverviewPage = lazy(() =>
  import('./features/pricing-workspace/components/NewDealOverviewPage').then((m) => ({
    default: m.NewDealOverviewPage,
  }))
);
const PipelineKanbanPage = lazy(() =>
  import('./features/pricing-workspace/pipeline/PipelineKanbanPage').then((m) => ({
    default: m.PipelineKanbanPage,
  }))
);
const SmartInboxPage = lazy(() =>
  import('./features/pricing-workspace/smart-inbox/SmartInboxPage').then((m) => ({
    default: m.SmartInboxPage,
  }))
);
const QuotePreviewSendPage = lazy(() =>
  import('./features/pricing-workspace/preview-send/QuotePreviewSendPage').then((m) => ({
    default: m.QuotePreviewSendPage,
  }))
);
const SupplierScorecardsPage = lazy(() =>
  import('./features/pricing-workspace/scorecards/SupplierScorecardsPage').then((m) => ({
    default: m.SupplierScorecardsPage,
  }))
);
const BillPreviewSendPage = lazy(() =>
  import('./features/bills/preview-send/BillPreviewSendPage').then((m) => ({
    default: m.BillPreviewSendPage,
  }))
);
const AiDashboardPage = lazy(() =>
  import('./pages/AiDashboardPage').then((m) => ({ default: m.AiDashboardPage }))
);
const DekelConnectPage = lazy(() =>
  import('./pages/DekelConnectPage').then((m) => ({ default: m.DekelConnectPage }))
);
const DekelPage = lazy(() => import('./pages/DekelPage').then((m) => ({ default: m.DekelPage })));

function PageFallback() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

/** Gates Gmail-only routes (inbound inbox); tenants without `features.gmail` bounce to /quotes. */
function GmailGate({ children }: { children: ReactNode }) {
  const { enabled, isLoading } = useGmailEnabled();
  if (isLoading) return <PageFallback />;
  if (!enabled) return <Navigate to="/quotes" replace />;
  return <>{children}</>;
}

function App() {
  return (
    // React Router v7 wraps history updates in startTransition by default. That defers route
    // commits and interacts badly with react-firebase-hooks listeners (long spinners on SPA
    // navigation; full refresh is fine). Disable transition-wrapped navigations for this app.
    <BrowserRouter unstable_useTransitions={false}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/supplier-quote/:token"
          element={
            <Suspense fallback={<PageFallback />}>
              <SupplierQuotePage />
            </Suspense>
          }
        />
        <Route
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DealsPage />} />
          {/* NewDealOverviewPage dispatches to DealWorkspacePage when variant='legacy'. */}
          <Route path="/deals/:dealId" element={<NewDealOverviewPage />} />
          <Route path="/deals/:dealId/pricing" element={<QuotePricingPage />} />
          <Route path="/deals/:dealId/supplier-pricing" element={<SupplierPricingPage />} />
          <Route path="/deals/:dealId/bids" element={<BidRequestsIndexPage />} />
          <Route path="/deals/:dealId/bids/:bidId" element={<BidRequestDetailPage />} />
          <Route
            path="/deals/:dealId/bids/:bidId/suppliers/:supplierId/entry"
            element={<BidRequestEntryPage />}
          />
          <Route path="/deals/:dealId/boq" element={<BoqIntakePage />} />
          <Route path="/deals/:dealId/labor" element={<LaborCalculationPage />} />
          <Route path="/deals/:dealId/pricing-matrix" element={<PricingMatrixPage />} />
          <Route path="/deals/:dealId/quote/preview" element={<QuotePreviewSendPage />} />
          <Route path="/bills/:billId/preview" element={<BillPreviewSendPage />} />
          <Route path="/pipeline" element={<PipelineKanbanPage />} />
          <Route path="/suppliers/scorecards" element={<SupplierScorecardsPage />} />
          <Route path="/ai" element={<AiDashboardPage />} />
          <Route path="/dekel" element={<DekelPage />} />
          <Route path="/dekel-connect" element={<DekelConnectPage />} />
          <Route path="/quotes" element={<QuotesPage />} />
          <Route path="/quick-quote" element={<QuickQuotePage />} />
          <Route path="/bills" element={<BillsPage />} />
          <Route path="/bills/:billId" element={<BillPage />} />
          <Route path="/quick-bill" element={<QuickBillPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/inbound-emails"
            element={
              <GmailGate>
                <SmartInboxPage />
              </GmailGate>
            }
          />
          <Route
            path="/inbound-emails/classic"
            element={
              <GmailGate>
                <InboundEmailsPage />
              </GmailGate>
            }
          />
          {/* Legacy redirects */}
          <Route path="/cost-estimates" element={<Navigate to="/" replace />} />
          <Route path="/bid-requests" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
